import {useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import React, {type SyntheticEvent, type FunctionComponent, type HTMLAttributes} from 'react'
import {getDomainConfig} from '~/domains'
import {ChosenByCheckIcon} from '~/icons/product-page/chosen-by-check-icon'
import {IconAmazonLogoSvg} from '~/icons/product-page/icon-amazon-logo-svg'
import {ValueForMoneyTagIcon} from '~/icons/value-for-money-tag'
import {longWordsLanguages, getTranslation} from '~/localization/translation'
import type {Product, RootLoaderResponse} from '~/types'
import VariantsIndication from '../badges/variants-indication'
import SaleEventBadge from '../badges/saleEventBadge'

interface DeafultCtaProps extends HTMLAttributes<HTMLDivElement> {
  product: Product
  showDealSpecs: boolean
  showDealPriceDropBadge: boolean
  redirectUrl: string
  showPrimeDayLogo?: boolean
  showVariantsIndication?: boolean
  reportVariantsClicked: () => void
  handleCardClick: (e: SyntheticEvent) => void
  index: number
}

const DeafultCta: FunctionComponent<DeafultCtaProps> = ({
  product,
  showDealSpecs,
  showPrimeDayLogo,
  showDealPriceDropBadge,
  redirectUrl,
  handleCardClick,
  showVariantsIndication,
  reportVariantsClicked,
  index,
  ...attributes
}: DeafultCtaProps) => {
  const {featureFlags, language, locale, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {styles: stylesBydomain, domainType} = getDomainConfig(domain, locale)
  const {brand = '', discount} = product
  return (
    <div {...attributes}>
      {/* ------------------------------------------------  Deal Specs MOBILE (Deals domain type)------------------------------------------------ */}
      {showDealSpecs && (
        <div className="mx-auto mt-4 hidden w-full flex-col gap-2 rounded-lg bg-green-50 p-4 shadow-md tablet:flex">
          {product?.dealsSpecs?.map((spec, index) => (
            <div
              key={`${spec}-${index}`}
              className="flex flex-row items-start gap-2 text-sm font-normal text-[#030303]"
            >
              <ValueForMoneyTagIcon className="shrink-0 [&>path]:fill-[#00B362]" />
              {spec}
            </div>
          ))}
        </div>
      )}
      {showPrimeDayLogo && (
        <SaleEventBadge
          redirectUrl={redirectUrl}
          handleCardClick={handleCardClick}
          brand={brand}
          index={index}
          placement="brg-card"
        />
      )}
      {featureFlags?.useReorganizedCardLayout && product.chosenBy && index === 0 ? (
        <div className="mx-auto mt-3 hidden flex-row items-center gap-1 tablet:flex">
          <ChosenByCheckIcon />
          <p className="text-sm font-light text-[#64748B]">{translations.Badge.chosenBy(product.chosenBy)}</p>
        </div>
      ) : null}

      <div className="z-20 flex w-full flex-col desktop:gap-2">
        {/* ------------------------------------------------  Big Price Drop Badge DESKTOP (Deals domain type) ------------------------------------------------ */}
        {showDealPriceDropBadge && (
          <div className="mx-auto flex w-fit flex-row items-center justify-center gap-2 rounded-lg bg-purple-50 px-4 py-2 text-sm font-medium shadow-md tablet:hidden">
            <ValueForMoneyTagIcon className="shrink-0 [&>path]:fill-purple-500" />
            {translations.Badge.bigPriceDrop}
          </div>
        )}
        <div className="flex w-full flex-row items-center justify-between gap-4 desktop:justify-center">
          {featureFlags?.showEngagingElements || featureFlags?.useReorganizedCardLayout ? (
            <a
              className="mt-1 hidden h-6 w-[80px] object-scale-down tablet:block"
              href={redirectUrl}
              onClick={handleCardClick}
              aria-label={`view ${brand}`}
              target="_blank"
              rel="sponsored noreferrer"
            >
              <IconAmazonLogoSvg className="h-6 w-[80px] object-scale-down" loading={index > 0 ? 'lazy' : 'eager'} />
            </a>
          ) : null}

          {/* ------------------------------------------------ CTA BUTTON------------------------------------------------ */}
          <a
            href={redirectUrl}
            onClick={handleCardClick}
            aria-label={`view ${brand}`}
            target="_blank"
            rel="sponsored noreferrer"
            className={clsx(
              'flex h-10 w-full cursor-pointer items-center justify-center rounded-3xl text-center uppercase',
              longWordsLanguages.includes(language) ? 'px-1 text-base desktop:text-sm' : 'text-base',
              stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
              'listpage_button_primary',
            )}
          >
            {translations.ProductCard.ctaText(
              domainType === 'deals' && !!discount && discount > 0 ? 'deals' : undefined,
            )}
          </a>
        </div>

        {/* ------------------------------------------------ Big Price Drop Badge MOBILE (Deals domain type) ------------------------------------------------ */}
        {showDealPriceDropBadge && (
          <div className="mx-auto mt-4 hidden w-full flex-row items-center justify-center gap-2 rounded-lg bg-purple-50 p-1 text-sm font-medium shadow-md tablet:flex">
            <ValueForMoneyTagIcon className="shrink-0 [&>path]:fill-purple-500" />
            {translations.Badge.bigPriceDrop}
          </div>
        )}
      </div>
      {/* ------------------------------------------------ Variants Mobile------------------------------------------------ */}
      <a
        href={redirectUrl}
        onClick={handleCardClick}
        aria-label={`view ${brand}`}
        target="_blank"
        rel="sponsored noreferrer"
        className={clsx(
          'flex flex-row items-center gap-1 desktop:hidden tablet:mt-2',
          (featureFlags?.showEngagingElements || featureFlags?.useReorganizedCardLayout) && 'mb-4',
        )}
      >
        {showVariantsIndication && (
          <VariantsIndication
            className="flex flex-row gap-1 truncate"
            index={index + 1}
            variations={product?.variationDetails}
            reportVariantsClicked={reportVariantsClicked}
          />
        )}
      </a>
    </div>
  )
}

export default DeafultCta
